import { useState } from 'react';
import { deleteAccount } from '@/services/accountService';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import styles from './DeleteAccountModal.module.css';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'warning' | 'confirm' | 'deleting';

export function DeleteAccountModal({ isOpen, onClose }: DeleteAccountModalProps) {
  const [step, setStep] = useState<Step>('warning');
  const [error, setError] = useState<string | null>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleClose = () => {
    setStep('warning');
    setError(null);
    onClose();
  };

  const handleProceedToConfirm = () => {
    setStep('confirm');
  };

  const handleDelete = async () => {
    setStep('deleting');
    setError(null);

    const result = await deleteAccount();

    if (result.success) {
      await signOut();
      handleClose();
      navigate('/');
    } else {
      setError(result.error || 'Failed to delete account');
      setStep('confirm');
    }
  };

  return (
    <div className={styles.overlay} onClick={handleClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {step === 'warning' && (
          <>
            <h2 className={styles.title}>Delete Account</h2>
            <p className={styles.message}>
              This will permanently delete:
            </p>
            <ul className={styles.list}>
              <li>Your profile and username</li>
              <li>All lesson history</li>
              <li>All progress data</li>
              <li>All race participation records</li>
            </ul>
            <p className={styles.warning}>This action cannot be undone.</p>
            <div className={styles.buttons}>
              <button className={styles.cancelButton} onClick={handleClose}>
                Cancel
              </button>
              <button className={styles.deleteButton} onClick={handleProceedToConfirm}>
                Delete Account
              </button>
            </div>
          </>
        )}

        {step === 'confirm' && (
          <>
            <h2 className={styles.title}>Final Confirmation</h2>
            <p className={styles.message}>
              To confirm deletion, please understand that all your data will be
              permanently removed and cannot be recovered.
            </p>
            {error && <p className={styles.error}>{error}</p>}
            <div className={styles.buttons}>
              <button className={styles.cancelButton} onClick={handleClose}>
                Cancel
              </button>
              <button className={styles.deleteButton} onClick={handleDelete}>
                Yes, Delete My Account
              </button>
            </div>
          </>
        )}

        {step === 'deleting' && (
          <>
            <h2 className={styles.title}>Deleting Account...</h2>
            <p className={styles.message}>Please wait while we delete your account.</p>
          </>
        )}
      </div>
    </div>
  );
}
